import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { getAxiosInstance } from "../../redux/common";

const BASE = "/api/admin/outbound-messages";

const STATUS_LABELS = {
  RETRY_PENDING: "Oczekuje na ponowienie",
  REPLAYING: "W trakcie ponowienia",
  SENT: "Wysłano",
  PERMANENT_FAILURE: "Błąd trwały",
  GAVE_UP: "Porzucono (za stare)",
  ON_HOLD: "Wstrzymano",
  CANCELLED: "Anulowano",
};

const CHANNELS = ["MAIL", "SMS"];
const CHANNEL_LABELS = { MAIL: "E-mail", SMS: "SMS" };

const EMPTY_FILTER = {
  status: "",
  channel: "",
  recipient: "",
  from: "",
  to: "",
};

// datetime-local gives local time without a zone; the backend expects an ISO instant.
const toInstant = (value) => (value ? new Date(value).toISOString() : null);

const toRequestFilter = (filter) => ({
  status: filter.status || null,
  channel: filter.channel || null,
  recipient: filter.recipient.trim() || null,
  from: toInstant(filter.from),
  to: toInstant(filter.to),
});

const toQueryParams = (filter) =>
  Object.fromEntries(
    Object.entries(toRequestFilter(filter)).filter(([, v]) => v !== null)
  );

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("pl-PL") : "—";

const errorText = (e) => {
  const status = e?.response?.status;
  if (status === 403) {
    return "Brak uprawnień — ta sekcja jest dostępna tylko dla operatora platformy.";
  }
  if (status === 409) {
    return `Operacja niemożliwa: ${
      e.response.data?.message || "wiadomość jest już zamknięta"
    }.`;
  }
  return `Błąd komunikacji z serwerem (${e?.message || status || "nieznany"}).`;
};

const canCancel = (row) => row.status !== "SENT" && row.status !== "CANCELLED";

function StatsTable({ stats }) {
  const count = (channel, status) =>
    stats.find((s) => s.channel === channel && s.status === status)?.count || 0;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Status</TableCell>
          {CHANNELS.map((c) => (
            <TableCell key={c} align="right">
              {CHANNEL_LABELS[c]}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.keys(STATUS_LABELS).map((status) => (
          <TableRow key={status}>
            <TableCell>{STATUS_LABELS[status]}</TableCell>
            {CHANNELS.map((c) => (
              <TableCell
                key={c}
                align="right"
                data-testid={`outbound-stat-${c}-${status}`}
              >
                {count(c, status)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function OutboundMessages() {
  const [filter, setFilter] = useState(EMPTY_FILTER);
  // Filter actually used for the list and bulk actions; edits apply on "Szukaj".
  const [applied, setApplied] = useState(EMPTY_FILTER);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [list, counts] = await Promise.all([
        getAxiosInstance().get(BASE, {
          params: { ...toQueryParams(applied), page, size },
        }),
        getAxiosInstance().get(`${BASE}/stats`),
      ]);
      setResult(list.data);
      setStats(counts.data);
      setError("");
    } catch (e) {
      setResult(null);
      setError(errorText(e));
    }
  }, [applied, page, size]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (request, describe) => {
    try {
      const response = await request();
      setNotice(describe(response));
      setError("");
    } catch (e) {
      setNotice("");
      setError(errorText(e));
    }
    await load();
  };

  const retryOne = (row) =>
    run(
      () => getAxiosInstance().post(`${BASE}/${row.id}/retry`),
      () => `Wiadomość do ${row.recipient} wróciła do kolejki.`
    );

  const askCancel = (row) =>
    setConfirm({
      text: `Anulować wiadomość do ${row.recipient}? Zapisana treść zostanie usunięta i nie da się jej już wysłać.`,
      action: () =>
        run(
          () => getAxiosInstance().post(`${BASE}/${row.id}/cancel`),
          () => `Anulowano wiadomość do ${row.recipient}.`
        ),
    });

  const askBulk = (path, verb) =>
    setConfirm({
      text: `${verb} wszystkie wiadomości pasujące do bieżącego filtra?`,
      action: () =>
        run(
          () =>
            getAxiosInstance().post(
              `${BASE}/${path}`,
              toRequestFilter(applied)
            ),
          (r) => `Zaktualizowano wiadomości: ${r.data.updated}.`
        ),
    });

  const onConfirm = async () => {
    const { action } = confirm;
    setConfirm(null);
    await action();
  };

  const search = (e) => {
    e.preventDefault();
    setPage(0);
    setApplied(filter);
  };

  const clear = () => {
    setFilter(EMPTY_FILTER);
    setPage(0);
    setApplied(EMPTY_FILTER);
  };

  const field = (name) => ({
    value: filter[name],
    onChange: (e) => setFilter({ ...filter, [name]: e.target.value }),
    inputProps: { "data-testid": `outbound-filter-${name}` },
    variant: "outlined",
    size: "small",
  });

  return (
    <Box p={1}>
      <Typography variant="h6">Niedostarczone e-maile i SMS-y</Typography>

      {error && (
        <Alert severity="error" data-testid="outbound-error">
          {error}
        </Alert>
      )}
      {notice && <Alert data-testid="outbound-notice">{notice}</Alert>}

      {result && (
        <>
          <Box mt={1} maxWidth={480}>
            <StatsTable stats={stats} />
          </Box>

          <form onSubmit={search} noValidate autoComplete="off">
            <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              <TextField
                select
                label="Status"
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                {...field("status")}
              >
                <option value="">Wszystkie</option>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                label="Kanał"
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                {...field("channel")}
              >
                <option value="">Wszystkie</option>
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {CHANNEL_LABELS[c]}
                  </option>
                ))}
              </TextField>
              <TextField label="Odbiorca" {...field("recipient")} />
              <TextField
                label="Pierwszy błąd od"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                {...field("from")}
              />
              <TextField
                label="Pierwszy błąd do"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                {...field("to")}
              />
              <Button
                type="submit"
                variant="contained"
                data-testid="outbound-search"
              >
                Szukaj
              </Button>
              <Button onClick={clear}>Wyczyść</Button>
            </Box>
          </form>

          <Box mt={2} display="flex" style={{ gap: 8 }}>
            <Button
              size="small"
              variant="outlined"
              data-testid="outbound-bulk-retry"
              onClick={() => askBulk("retry", "Ponowić")}
            >
              Ponów pasujące
            </Button>
            <Button
              size="small"
              variant="outlined"
              data-testid="outbound-bulk-hold"
              onClick={() => askBulk("hold", "Wstrzymać")}
            >
              Wstrzymaj pasujące
            </Button>
            <Button
              size="small"
              variant="outlined"
              data-testid="outbound-bulk-release"
              onClick={() => askBulk("release", "Wznowić")}
            >
              Wznów wstrzymane
            </Button>
          </Box>

          <Table size="small" data-testid="outbound-table">
            <TableHead>
              <TableRow>
                <TableCell>Kanał</TableCell>
                <TableCell>Odbiorca</TableCell>
                <TableCell>Temat</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Próby</TableCell>
                <TableCell>Błąd</TableCell>
                <TableCell>Pierwszy błąd</TableCell>
                <TableCell>Następna próba / wysłano</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {result.content.map((row) => (
                <TableRow key={row.id} data-testid={`outbound-row-${row.id}`}>
                  <TableCell>
                    {CHANNEL_LABELS[row.channel] || row.channel}
                  </TableCell>
                  <TableCell>{row.recipient}</TableCell>
                  <TableCell>{row.subject || "—"}</TableCell>
                  <TableCell>
                    {STATUS_LABELS[row.status] || row.status}
                  </TableCell>
                  <TableCell align="right">{row.attempts}</TableCell>
                  <TableCell title={row.errorMessage || ""}>
                    {row.errorCode || row.errorClass || "—"}
                  </TableCell>
                  <TableCell>{formatDate(row.firstFailedAt)}</TableCell>
                  <TableCell>
                    {formatDate(
                      row.status === "SENT" ? row.sentAt : row.nextAttemptAt
                    )}
                  </TableCell>
                  <TableCell>
                    {row.retryable && (
                      <Button
                        size="small"
                        data-testid={`outbound-retry-${row.id}`}
                        onClick={() => retryOne(row)}
                      >
                        Ponów
                      </Button>
                    )}
                    {canCancel(row) && (
                      <Button
                        size="small"
                        data-testid={`outbound-cancel-${row.id}`}
                        onClick={() => askCancel(row)}
                      >
                        Anuluj
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {result.content.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>Brak wiadomości.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={result.totalElements}
            page={page}
            rowsPerPage={size}
            rowsPerPageOptions={[20, 50, 100]}
            labelRowsPerPage="Na stronę:"
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>Potwierdź</DialogTitle>
        <DialogContent>{confirm?.text}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Nie</Button>
          <Button
            color="primary"
            data-testid="outbound-confirm"
            onClick={onConfirm}
          >
            Tak
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OutboundMessages;
